import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { redirectExternal } from "@/lib/redirect";
import { toast } from "sonner";
import { MEMBERSHIP_TIERS, DIGITAL_ID_PRICE, DIGITAL_ID_LOOKUP_KEY } from "@/data/pjp";
import { IdCard, ShieldCheck, ArrowRight, Loader2, Sparkles, Nfc, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// Extra tiers sold via Stripe but not exposed on /membership (JumpSeat Rider,
// etc). Keeps the AddonUpsell page self-sufficient regardless of entry point.
const EXTRA_TIERS = [
  { slug: "empty-leg-rider", lookup_key: "pjp_empty_leg_rider_yearly", label: "JumpSeat Rider", price: 10000, billing: "yearly" },
];
const ALL_TIERS = [...MEMBERSHIP_TIERS, ...EXTRA_TIERS];

/**
 * AddonUpsell — the interstitial that fires between "tier picked" and Stripe
 * checkout. Shows a single-decision Digital ID upsell so the shopper enters
 * their card once and gets both perks in one Stripe session.
 *
 * URL: /membership/addon?tier=<slug>
 * Reachable from: (a) Membership.jsx JOIN buttons, (b) AuthPage.jsx post-register
 * replay of a stashed tier lookup.
 */
export default function AddonUpsell() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const tierSlug = params.get("tier");
  const tier = useMemo(() => ALL_TIERS.find((t) => t.slug === tierSlug), [tierSlug]);
  const [busy, setBusy] = useState(null); // "with" | "without"

  // If tier param is missing/invalid, bounce back to the picker (in an effect
  // so we never call nav() during render).
  React.useEffect(() => {
    if (!tier) nav("/membership", { replace: true });
  }, [tier, nav]);
  if (!tier) return null;

  const totalWith = tier.price + DIGITAL_ID_PRICE;
  const billingCadence = tier.billing === "yearly" ? "yr" : "mo";

  const startCheckout = async (withAddon) => {
    if (!user) {
      // Shouldn't happen — the Membership flow only routes here for logged-in
      // users. Fall back to /register with the stash preserved.
      sessionStorage.setItem("pjp_pending_checkout", tier.lookup_key);
      if (withAddon) sessionStorage.setItem("pjp_pending_addon", DIGITAL_ID_LOOKUP_KEY);
      nav(`/register?tier=${tier.slug}`);
      return;
    }
    setBusy(withAddon ? "with" : "without");
    try {
      const body = {
        lookup_key: tier.lookup_key,
        origin_url: window.location.origin,
      };
      if (withAddon) body.addon_lookup_keys = [DIGITAL_ID_LOOKUP_KEY];
      const r = await api.post("/payments/checkout", body);
      if (!redirectExternal(r.data.checkout_url)) {
        setBusy(null);
        toast.error("Popup blocked. Please allow pop-ups and try again.");
      }
    } catch (e) {
      setBusy(null);
      toast.error(e?.response?.data?.detail || "Could not start checkout");
    }
  };

  return (
    <div className="min-h-[85vh] px-6 py-14 hud-grid">
      <div className="max-w-5xl mx-auto">
        {/* Progress rail */}
        <div className="flex items-center gap-3 mono text-[10px] tracking-[0.28em] uppercase text-white/50 mb-6" data-testid="addon-progress">
          <span className="text-cyan flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-cyan shadow-[0_0_6px_rgba(75,156,255,0.9)]" /> Tier selected</span>
          <span className="h-px flex-1 bg-white/10" />
          <span className="text-cyan flex items-center gap-1.5"><span className="relative inline-flex h-1.5 w-1.5"><span className="absolute inline-flex h-full w-full rounded-full bg-cyan opacity-70 animate-ping" /><span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-cyan" /></span> Add your badge?</span>
          <span className="h-px flex-1 bg-white/10" />
          <span className="text-white/35">Payment</span>
          <span className="h-px flex-1 bg-white/10" />
          <span className="text-white/35">Cockpit</span>
        </div>

        <div className="grid md:grid-cols-12 gap-8 items-start">
          {/* LEFT — the pitch */}
          <div className="md:col-span-7 space-y-6">
            <div>
              <div className="overline text-orange-hud mb-2">// One-time · Add-on</div>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.02]">
                Make it official.<br /><span className="text-orange-hud">Add your Digital ID.</span>
              </h1>
              <p className="mt-4 text-white/70 text-[15px] leading-relaxed max-w-xl">
                The PJP Digital ID is the wallet-ready, NFC-scannable, FAA cross-checked badge every
                <span className="text-white/90"> operator, FBO, and dispatcher</span> already recognizes.
                Ships to any active PJP member — pair it with your <span className="text-cyan">{tier.label}</span> membership
                and it activates <span className="text-white/90">the same day your card clears</span>.
              </p>
            </div>

            {/* Perk grid */}
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { icon: ShieldCheck, title: "FAA cross-checked", desc: "Airmen Registry live-verified with a neon-green banner on your public badge." },
                { icon: Nfc,         title: "NFC + QR scannable", desc: "One tap on an FBO iPad or dispatcher phone loads your live profile." },
                { icon: IdCard,      title: "Wallet-ready",       desc: "Add to Apple Wallet / Google Wallet — always in your pocket, offline." },
                { icon: Sparkles,    title: "Ships worldwide",    desc: "Physical NFC card + a downloadable digital variant. Free replacement if damaged." },
              ].map((p) => (
                <div key={p.title} className="rounded-lg border border-white/[0.08] bg-white/[0.02] p-4 hover:border-orange-hud/30 transition-colors group" data-testid={`addon-perk-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <p.icon className="h-4 w-4 text-orange-hud group-hover:scale-110 transition-transform" />
                    <div className="font-display text-[13px] font-semibold text-white">{p.title}</div>
                  </div>
                  <div className="text-[12px] text-white/60 leading-relaxed">{p.desc}</div>
                </div>
              ))}
            </div>

            {/* Social proof strip */}
            <div className="mono text-[10px] tracking-[0.24em] uppercase text-white/45 flex flex-wrap items-center gap-x-4 gap-y-1">
              <span className="text-cyan">26,511</span> members
              <span className="h-3 w-px bg-white/10" />
              <span className="text-cyan">1,240</span> operators recognize it
              <span className="h-3 w-px bg-white/10" />
              <span className="text-cyan">87%</span> of pilots keep theirs on their kit bag
            </div>

            <div className="text-[11px] text-white/40">
              You'll enter your card just once — Stripe combines your <span className="text-white/70">{tier.label}</span> membership
              and your Digital ID into a single receipt.
            </div>
          </div>

          {/* RIGHT — the decision */}
          <div className="md:col-span-5">
            <div className="relative rounded-2xl border border-orange-hud/40 bg-obsidian/85 backdrop-blur-md p-6 md:p-7 overflow-hidden shadow-[0_0_50px_-14px_rgba(255,138,61,0.35)]" data-testid="addon-decision-card">
              {/* Ambient glow */}
              <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 100% 0%, rgba(255,138,61,0.18), transparent 55%), radial-gradient(circle at 0% 100%, rgba(75,156,255,0.10), transparent 55%)" }} />
              <span className="hud-c-tl" /><span className="hud-c-tr" />
              <span className="hud-c-bl" /><span className="hud-c-br" />

              <div className="relative">
                <div className="mono text-[10px] tracking-[0.28em] uppercase text-orange-hud mb-2 flex items-center gap-2">
                  <IdCard className="h-3.5 w-3.5" /> // YOUR ORDER
                </div>

                {/* Line items */}
                <div className="space-y-2.5 mb-4">
                  <div className="flex items-baseline justify-between" data-testid="line-item-tier">
                    <div>
                      <div className="text-[13px] text-white/85 font-medium">{tier.label} membership</div>
                      <div className="mono text-[10px] text-white/45 tracking-widest uppercase">Recurring · {billingCadence}</div>
                    </div>
                    <div className="mono text-lg text-white">${tier.price.toLocaleString()}</div>
                  </div>
                  <div className="h-px bg-white/[0.06]" />
                  <div className="flex items-baseline justify-between opacity-95" data-testid="line-item-addon">
                    <div>
                      <div className="text-[13px] text-orange-hud font-medium flex items-center gap-1">
                        + Digital ID Badge
                      </div>
                      <div className="mono text-[10px] text-white/45 tracking-widest uppercase">Per year</div>
                    </div>
                    <div className="mono text-lg text-orange-hud">${DIGITAL_ID_PRICE}<span className="text-[10px] font-normal text-white/40">/yr</span></div>
                  </div>
                  <div className="h-px bg-white/[0.06]" />
                  <div className="flex items-baseline justify-between pt-1">
                    <div className="mono text-[10px] tracking-widest uppercase text-white/55">Total today</div>
                    <div className="font-display text-2xl font-bold text-white">
                      ${totalWith.toLocaleString()}
                      <span className="text-[11px] text-white/50 font-normal ml-1">USD</span>
                    </div>
                  </div>
                </div>

                {/* Primary CTA — add it */}
                <button
                  type="button"
                  onClick={() => startCheckout(true)}
                  disabled={busy !== null}
                  data-testid="btn-addon-yes"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 bg-orange-hud text-obsidian font-display font-semibold tracking-wide shadow-[0_0_20px_-4px_rgba(255,138,61,0.6)] hover:shadow-[0_0_28px_-2px_rgba(255,138,61,0.9)] hover:brightness-105 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                >
                  {busy === "with" ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                    <>
                      YES, ADD THE BADGE <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>

                <div className="mt-2 flex items-center justify-center gap-1.5 mono text-[10px] tracking-widest uppercase text-white/45">
                  <Check className="h-3 w-3 text-avail" /> Ships free · verified in 24-48h
                </div>

                {/* Secondary CTA — skip */}
                <button
                  type="button"
                  onClick={() => startCheckout(false)}
                  disabled={busy !== null}
                  data-testid="btn-addon-skip"
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 bg-transparent border border-white/15 text-white/70 text-[13px] hover:border-white/30 hover:text-white transition-colors disabled:opacity-60"
                >
                  {busy === "without" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (
                    <>No thanks — just {tier.label} membership (${tier.price.toLocaleString()})</>
                  )}
                </button>

                {/* Trust line */}
                <div className="mt-5 pt-4 border-t border-white/[0.06] mono text-[10px] tracking-widest uppercase text-white/40 text-center">
                  Secure Stripe checkout · Cancel anytime
                </div>
              </div>
            </div>

            {/* Badge preview link */}
            <div className="mt-4 text-center">
              <Link to="/digital-id/demo" data-testid="addon-preview-badge" className="mono text-[10px] tracking-widest uppercase text-cyan hover:text-white inline-flex items-center gap-1">
                See a live badge preview <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
